import { FC, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import LoadingSpinner from "@/components/LoadingSpinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ListPromptVersion } from "@/types";

interface BacktestModalProps {
  isOpen: boolean;
  onSubmit: (backtestVersionId: number, logsVersionId: number | null) => void;
  setOpen: (isOpen: boolean) => void;
  versions: ListPromptVersion[];
  selectedVersionId: number;
  isLoading: boolean;
}

const BacktestModal: FC<BacktestModalProps> = ({
  isOpen,
  onSubmit,
  setOpen,
  versions,
  selectedVersionId,
  isLoading,
}) => {
  const [backtestVersionId, setBacktestVersionId] = useState<number>(selectedVersionId);
  const [logsVersionId, setLogsVersionId] = useState<number | null>(null);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (isOpen && !hasInitialized.current) {
      // Pre-select the latest version to evaluate
      const latestVersion = versions.reduce((latest, current) =>
        current.number > latest.number ? current : latest,
      );
      setBacktestVersionId(latestVersion.id);
      // Pre-select the clicked version for historical logs
      setLogsVersionId(selectedVersionId);
      hasInitialized.current = true;
    }
  }, [isOpen, selectedVersionId, versions]);

  useEffect(() => {
    // Reset when modal is closed
    if (!isOpen) {
      hasInitialized.current = false;
      setLogsVersionId(null);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    onSubmit(backtestVersionId, logsVersionId);
  };

  const getVersionLabel = (versionId: number) => {
    const version = versions.find(v => v.id === versionId);
    if (!version) {
      return "Unknown Version";
    }

    const latestVersion = versions.reduce((latest, current) =>
      current.number > latest.number ? current : latest,
    );
    const isLatest = version.id === latestVersion.id;

    return `Version ${version.number}${isLatest ? " (latest)" : ""}`;
  };

  return (
    <Dialog onOpenChange={setOpen} open={isOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Run a backtest</DialogTitle>
          <DialogDescription>
            <span className="max-w-md">
              Evaluate performance with historical data by comparing prompt versions.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Version to evaluate</label>
            <DropdownMenu>
              <DropdownMenuTrigger className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-left shadow-sm transition-all duration-200 hover:border-blue-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50">
                {getVersionLabel(backtestVersionId)}
              </DropdownMenuTrigger>
              <DropdownMenuContent className="mt-1 max-h-60 w-full overflow-auto rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                {versions.map(version => (
                  <DropdownMenuItem
                    key={version.id}
                    onSelect={() => setBacktestVersionId(version.id)}
                    className="cursor-pointer px-4 py-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-blue-50 hover:text-blue-700"
                  >
                    Version {version.number}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Using historical logs from version
            </label>
            <DropdownMenu>
              <DropdownMenuTrigger className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-left shadow-sm transition-all duration-200 hover:border-blue-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50">
                {logsVersionId ? getVersionLabel(logsVersionId) : "Select version"}
              </DropdownMenuTrigger>
              <DropdownMenuContent className="mt-1 max-h-60 w-full overflow-auto rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                <DropdownMenuItem
                  onSelect={() => setLogsVersionId(null)}
                  className="cursor-pointer px-4 py-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-blue-50 hover:text-blue-700"
                >
                  All versions
                </DropdownMenuItem>
                {versions.map(version => (
                  <DropdownMenuItem
                    key={version.id}
                    onSelect={() => setLogsVersionId(version.id)}
                    className="cursor-pointer px-4 py-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-blue-50 hover:text-blue-700"
                  >
                    Version {version.number}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => setOpen(false)} variant="secondary" disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? <LoadingSpinner size={4} /> : "Run"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BacktestModal;
